import React from "react";

import AssignmentMenu from "@Components/groups/AssignmentMenu";
import { ITag, ITagMinimal } from "@Components/groups/GroupUtils";
import GroupsListTag from "@Components/groups/GroupsListTag";
import MiniSpinner from "@Components/shared/MiniSpinner";
import { GroupContext, UserContext } from "@Constants";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { IRoleAssignmentPaged } from "@Models/IRoleDetails";
import { hasCapability } from "@Utils";
import { CloseCircleFilled } from "@ant-design/icons";
import { Popconfirm, Tooltip } from "antd";
import { IAddAction } from "./types";

interface IProps {
    addAction: IAddAction;
    addressTags: ITag[];
    creditAssignments: ICreditDetailsPaged[];
    creditTags: ITag[];
    fetching: boolean;
    fullName: string;
    group: string;
    index: number;
    isActive?: boolean;
    licenseTags: ITag[];
    page: number;
    removeAction?: (userId: string) => void;
    roleAssignments: IRoleAssignmentPaged[];
    roleTags: ITag[];
    statusTags: ITagMinimal[];
    select: (userId: string) => void;
    tagsLoading: boolean;
    userId: string;
}

const GroupListUserItem: React.FC<IProps> = ({
    addAction,
    addressTags,
    creditAssignments,
    creditTags,
    fetching,
    fullName,
    group,
    index,
    isActive,
    licenseTags,
    page,
    roleAssignments,
    roleTags,
    select,
    statusTags,
    userId,
    removeAction,
}: IProps) => {
    const smallTags =
        roleTags.length + addressTags.length + creditTags.length + licenseTags.length + statusTags.length > 5;

    const tags: JSX.Element[] = [];
    roleTags?.forEach((tag, tagIndex) => {
        tags.push(
            <GroupsListTag
                key={"g-" + group + "-u-" + userId + "-role-t-" + tag.entityId + "-i-" + tagIndex}
                index={tagIndex}
                tag={tag}
            />
        );
    });

    if (addressTags?.length > 0) {
        tags.push(
            <GroupsListTag key={"g-" + group + "-u-" + userId + "-address-t-0"} index={0} tag={addressTags[0]} />
        );
    }

    if (creditTags?.length > 0) {
        tags.push(<GroupsListTag key={"g-" + group + "-u-" + userId + "-credit-t-0"} index={0} tag={creditTags[0]} />);
    }

    if (licenseTags?.length > 0) {
        tags.push(
            <GroupsListTag key={"g-" + group + "-u-" + userId + "-license-t-0"} index={0} tag={licenseTags[0]} />
        );
    }

    statusTags?.forEach((tag, tagIndex) => {
        tags.push(
            <GroupsListTag key={"g-" + group + "-u-" + userId + "-status-t-" + tagIndex} index={tagIndex} tag={tag} />
        );
    });

    if (fetching)
        tags.push(
            <span
                key={"group-" + group + "spin-item-" + userId}
                style={{
                    display: "inline-block",
                    justifyContent: "left",
                    paddingLeft: 10,
                }}
            >
                <MiniSpinner />
            </span>
        );

    const allowRemove = hasCapability("UserGroup/RemoveMembers", GroupContext, group);

    return (
        <div
            key={"group-" + group + "user-item-" + userId}
            className={"groups-list-user-item " + (isActive ? "active " : "") + (index % 2 === 0 ? "even" : "odd")}
            onClick={() => select(userId)}
        >
            <table>
                <tbody>
                    <tr>
                        <td className="groups-list-user-item-name">{fullName}</td>
                        <td className={smallTags ? "small-tags" : ""}>
                            {tags}
                            {!fetching && addAction !== null ? (
                                <AssignmentMenu
                                    roleAssignments={roleAssignments}
                                    creditAssignments={creditAssignments}
                                    context={UserContext}
                                    groupId={group}
                                    page={page}
                                    userId={userId}
                                    addAction={addAction}
                                    systemRolesOnly={false}
                                />
                            ) : null}
                        </td>
                        <td className="groups-list-close-icon">
                            {allowRemove && !!removeAction ? (
                                <Tooltip title="Remove from group">
                                    <Popconfirm
                                        title={"Remove user from group and all child groups?"}
                                        onConfirm={() => removeAction(userId)}
                                        okText="Yes"
                                        cancelText="No"
                                        cancelButtonProps={{ className: "secondary" }}
                                    >
                                        <button className={"no-style"}>
                                            <CloseCircleFilled />
                                        </button>
                                    </Popconfirm>
                                </Tooltip>
                            ) : null}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
export default GroupListUserItem;
